import React from 'react';
import { cn } from '@/lib/utils';
import type { ResolvedCoverFields } from '../resolve-cover-fields';

export type CoverTemplateProps = {
    fields: ResolvedCoverFields;
    colors: {
        primary: string;
        secondary: string;
    };
    logoUrl?: string | null;
    showLogo: boolean;
};

export const COVER_SERIF_FONT = "Georgia, 'Times New Roman', serif";
export const COVER_SANS_FONT = "'Schibsted Grotesk', system-ui, sans-serif";

/**
 * Set by CoverPage on the A4 root. `--cover-display` is the title face
 * (cover.font when set, else the template's native display font);
 * `--cover-title-scale` is the title_size multiplier. Templates size their
 * title block with `coverTitleSize(em)` so both work on any template.
 */
export const COVER_DISPLAY_FONT_VAR = 'var(--cover-display)';

export const COVER_TITLE_SCALES = { small: 0.85, medium: 1, large: 1.15 } as const;

/** font-size for title-block text: base em × the title_size multiplier. */
export const coverTitleSize = (em: number) => `calc(${em}em * var(--cover-title-scale, 1))`;

/** #rrggbb (or #rgb) → rgba() with the given alpha, for brand-tinted strokes. */
export const withAlpha = (hex: string, alpha: number) => {
    const raw = hex.replace('#', '');
    const full =
        raw.length === 3
            ? raw
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : raw;
    const int = Number.parseInt(full, 16);
    if (Number.isNaN(int) || full.length !== 6) return hex;
    return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
};

/** Dim text over white paper vs over a solid primary background. */
export const COVER_DIM_ON_LIGHT = '#666e7d';
export const COVER_DIM_ON_DARK = 'rgba(255, 255, 255, 0.72)';

export const CoverLogo: React.FC<{
    logoUrl?: string | null;
    showLogo: boolean;
    className?: string;
}> = ({ logoUrl, showLogo, className }) => {
    if (!showLogo || !logoUrl) return null;

    return (
        <img
            src={logoUrl}
            alt=""
            className={cn('h-[3.2em] w-auto max-w-[9em] object-contain', className)}
        />
    );
};

export const coverPsychologistLine = (fields: ResolvedCoverFields) =>
    [fields.psychologist_name, fields.crp].filter(Boolean).join(' · ');

/**
 * The premium-generation meta stack: finer lines (0.62em) with the patient
 * emphasized. Templates with a bespoke meta layout (Editorial, Estrutura,
 * Lombada, Papelaria) compose from the resolved fields directly instead.
 */
export const CoverMetaSmall: React.FC<{
    fields: ResolvedCoverFields;
    dimColor?: string;
    className?: string;
}> = ({ fields, dimColor = COVER_DIM_ON_LIGHT, className }) => {
    const psychologist = coverPsychologistLine(fields);
    const dimLines = [fields.solicitante, psychologist || undefined, fields.city_date].filter(
        Boolean,
    );

    if (!fields.patient_name && dimLines.length === 0) return null;

    return (
        <div className={cn('text-[0.62em] leading-[1.8]', className)}>
            {fields.patient_name ? (
                <div className="text-[1.18em] font-semibold">{fields.patient_name}</div>
            ) : null}
            {dimLines.map((line) => (
                <div key={line} style={{ color: dimColor }}>
                    {line}
                </div>
            ))}
        </div>
    );
};

/**
 * The shared identification stack: patient, solicitante, psychologist · CRP,
 * city/date. Every template renders these the same way; only placement and
 * tone change.
 */
export const CoverMeta: React.FC<{
    fields: ResolvedCoverFields;
    tone?: 'light' | 'dark';
    className?: string;
}> = ({ fields, tone = 'light', className }) => {
    const dimColor = tone === 'dark' ? COVER_DIM_ON_DARK : COVER_DIM_ON_LIGHT;

    const psychologistLine = [fields.psychologist_name, fields.crp].filter(Boolean).join(' · ');

    const dimLines = [fields.solicitante, psychologistLine || undefined, fields.city_date].filter(
        Boolean,
    );

    if (!fields.patient_name && dimLines.length === 0) return null;

    return (
        <div className={cn('text-[0.82em] leading-[1.5]', className)}>
            {fields.patient_name ? (
                <div className="font-semibold">{fields.patient_name}</div>
            ) : null}
            {dimLines.map((line) => (
                <div key={line} style={{ color: dimColor }}>
                    {line}
                </div>
            ))}
        </div>
    );
};
